<template>
  <!-- trial-page class required -->
  <div class="page trial-page">
    <!-- hero - should be outside of .container -->
    <div class="mb-md-5">
      <div class="text-center">
        <div class="trial-hero p-4 text-center">
          <h2 class="text-primary">
            <strong>
              Ready to start saving time spent on clinical documentation?
            </strong>
          </h2>
          <h3 class="mb-5">
            Try Dragon Medical One, the industry's #1 documentation companion
            and workflow assistant.
          </h3>
        </div>
      </div>
    </div>
    <!-- / hero -->
    <div class="container trial-page full-width">
      <div class="row full-width">
        <div class="col-md-6 col-xs-12">
          <div class="bg-primary p-5 img-rounded mb-5">
            <form @submit.prevent="addLeadAccountForm">
              <h5 class="text-center mb-5 mt-0 text-secondary">
                Fill out the form below to start your
                <strong>FREE</strong> 7-day trial.
              </h5>
              <div v-if="errors.length" class="alert alert-danger">
                <b>Please correct the following error(s):</b>
                <ul style="list-style: none">
                  <li v-for="error in errors" :key="'error-' + error">
                    {{ error }}
                  </li>
                </ul>
              </div>
              <label for="first_name" class="sr-only">First Name:</label>
              <input
                id="first_name"
                v-model="first_name"
                type="text"
                name="first_name"
                value=""
                required="required"
                class="form-control mb-4 rounded"
                placeholder="First Name"
              />
              <label for="last_name" class="sr-only">Last Name:</label>
              <input
                id="last_name"
                v-model="last_name"
                type="text"
                name="last_name"
                value=""
                required="required"
                class="form-control mb-4 rounded"
                placeholder="Last Name"
              />
              <label for="phone" class="sr-only">Phone:</label>
              <input
                id="phone"
                ref="phone"
                v-model="phone"
                type="text"
                name="phone"
                value=""
                required="required"
                class="form-control mb-4 rounded"
                placeholder="Phone"
              />
              <label for="company" class="sr-only">Organization Name:</label>
              <input
                id="company"
                ref="company"
                v-model="company"
                type="text"
                name="company"
                value=""
                required="required"
                class="form-control mb-4 rounded"
                placeholder="Organization Name"
              />
              <label for="email" class="sr-only">Email:</label>
              <input
                id="email"
                v-model="email"
                type="email"
                name="email"
                value=""
                required="required"
                class="form-control mb-4 rounded"
                placeholder="Email Address"
              />
              <label for="password" class="sr-only">Password:</label>
              <input
                id="password"
                v-model="password"
                type="password"
                name="password"
                required="required"
                class="form-control mb-4 rounded"
                placeholder="Password"
                @keyup.prevent="validatePassword"
              />
              <label for="password_confirmation" class="sr-only">
                Confirm Password:
              </label>
              <input
                id="password_confirmation"
                v-model="password_confirmation"
                type="password"
                name="password_confirmation"
                required="required"
                class="form-control mb-4 rounded"
                placeholder="Confirm Password"
                @keyup.prevent="validateMatchingPassword"
              />
              <div class="row mb-2 mb-xs-0 password-requirements">
                <div class="col-md-6 col-sm-6 text-left">
                  <span
                    :class="[
                      'glyphicon',
                      passwordRules.uppercase
                        ? 'glyphicon-ok text-secondary'
                        : 'glyphicon-remove no-passed',
                    ]"
                  ></span>
                  <span style="font-weight: 100">
                    Must have an uppercase letter.
                  </span>
                </div>
                <div class="col-md-6 col-sm-6 text-left">
                  <span
                    :class="[
                      'glyphicon',
                      passwordRules.matching
                        ? 'glyphicon-ok text-secondary'
                        : 'glyphicon-remove no-passed',
                    ]"
                  ></span>
                  <span style="font-weight: 100">Confirm must match.</span>
                </div>
              </div>
              <div class="row mb-2 mb-xs-0 password-requirements">
                <div class="col-md-6 col-sm-6 text-left">
                  <span
                    :class="[
                      'glyphicon',
                      passwordRules.lowercase
                        ? 'glyphicon-ok text-secondary'
                        : 'glyphicon-remove no-passed',
                    ]"
                  ></span>
                  <!-- prettier-ignore -->
                  <span style="font-weight: 100">Must have a lowercase letter.</span>
                </div>
                <div class="col-md-6 col-sm-6 text-left">
                  <span
                    :class="[
                      'glyphicon',
                      passwordRules.symbol
                        ? 'glyphicon-ok text-secondary'
                        : 'glyphicon-remove no-passed',
                    ]"
                  ></span>
                  <span style="font-weight: 100">Must include a symbol.</span>
                </div>
              </div>
              <div class="row password-requirements">
                <div class="col-md-6 col-sm-6 text-left">
                  <span
                    :class="[
                      'glyphicon',
                      passwordRules.amount
                        ? 'glyphicon-ok text-secondary'
                        : 'glyphicon-remove no-passed',
                    ]"
                  ></span>
                  <!-- prettier-ignore -->
                  <span style="font-weight: 100">Must be at least 8 characters.</span>
                </div>
                <div class="col-md-6 col-sm-6 text-left">
                  <span
                    :class="[
                      'glyphicon',
                      passwordRules.lowercase
                        ? 'glyphicon-ok text-secondary'
                        : 'glyphicon-remove no-passed',
                    ]"
                  ></span>
                  <span style="font-weight: 100">Must include a number.</span>
                </div>
              </div>
              <p>
                <button
                  id="step5Register"
                  type="submit"
                  class="btn btn-primary btn-md-lg mt-4 btn-sm btn-block bg-secondary"
                  :disabled="!submitEnabled"
                >
                  Start My Free Trial »
                </button>
              </p>
            </form>
          </div>
        </div>
        <div class="col-md-6 col-xs-12">
          <img
            src="/img/trial-logo-dmo.svg"
            class="img-responsive mb-3"
            alt="Nuance Dragon Medical One"
          />
          <p class="trial-description">
            Fast, flexible, and future-proof Built on a modern architecture,
            Dragon® Medical One, the industry’s #1 rated documentation companion
            and workflow assistant, empowers clinicians to create comprehensive
            clinical documentation and more naturally navigate workflows using
            just their voice.
          </p>
          <hr />
          <div class="row mb-5">
            <div class="col-md-8">
              <p class="trial-description mb-0">
                <strong>
                  2022 and 2021 Ranked #1 Best in KLAS Speech Recognition -
                  Front-End EMR.
                </strong>
                Named 2022 and 2021 Best in KLAS, earning praise from clinicians
                for helping them deliver and document better patient care.
              </p>
            </div>
            <div class="col-md-4 text-center pt-5">
              <img src="/img/trial-logo-klas.svg" alt="KLAS" />
            </div>
          </div>
          <div class="embed-responsive embed-responsive-16by9 mb-5">
            <iframe
              src="https://www.youtube.com/embed/-8ccfeebfoQ"
              title="YouTube video player"
              class="embed-responsive-item"
              allowfullscreen
            ></iframe>
          </div>
        </div>
      </div>
      <div class="bg-secondary" style="border-radius: 10px">
        <div class="row">
          <div class="col-md-3 text-center">
            <div class="p-5">
              <p class="trial-stat">98%</p>
              <p class="lead">
                <strong>of physicians recommend Dragon Medical One</strong>
              </p>
            </div>
          </div>
          <div class="col-md-3 text-center">
            <div class="p-5">
              <p class="trial-stat">50%</p>
              <p class="lead">
                <strong>less time spent on clinical documentation</strong>
              </p>
            </div>
          </div>
          <div class="col-md-3 text-center">
            <div class="p-5">
              <p class="trial-stat">54%</p>
              <p class="lead">
                <strong>increase in productivity</strong>
              </p>
            </div>
          </div>
          <div class="col-md-3 text-center">
            <div class="p-5">
              <p class="trial-stat">93%</p>
              <p class="lead">
                <strong>easier to capture the complete patient story</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading">
      {{ notifyLoading() }}
    </div>
    <div v-if="errorAlert">
      {{ notifyError() }}
    </div>
    <br />
  </div>
</template>

<script>
import axios from "axios";
import swal from "sweetalert2";

export default {
  props: {
    varReseller: {
      type: String,
      default: "",
    },
    referer: {
      type: String,
      default: "",
    },
    urlReferer: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      first_name: "",
      last_name: "",
      company: "",
      email: "",
      phone: "",
      password: "",
      password_confirmation: "",
      registration_errors: [],
      passwordRules: {
        uppercase: false,
        lowercase: false,
        digits: false,
        symbol: false,
        matching: false,
        amount: false,
      },
      loading: false,
      error_alert: null,
    };
  },

  computed: {
    submitEnabled() {
      return (
        Boolean(this.first_name.length) &&
        Boolean(this.last_name.length) &&
        Boolean(this.company.length) &&
        Boolean(this.email.length) &&
        Boolean(this.phone.length) &&
        Boolean(this.password.length) &&
        Boolean(this.password_confirmation.length) &&
        this.passwordRules.amount &&
        this.passwordRules.matching &&
        this.passwordRules.digits &&
        this.passwordRules.symbol &&
        this.passwordRules.uppercase &&
        this.passwordRules.lowercase &&
        !this.loading
      );
    },
    errors() {
      return this.registration_errors;
    },
    errorAlert() {
      return this.error_alert;
    },
    isLoading() {
      return this.loading;
    },
  },

  methods: {
    clearErrors() {
      this.registration_errors = [];
    },

    pushError(msg) {
      this.registration_errors.push(msg);
    },

    async addLeadAccountForm() {
      this.switchLoadingTo(true);
      const formFields = this.getFormFields();
      await this.validateForm(formFields);
      if (this.registration_errors.length === 0) {
        setTimeout(() => {
          this.createNewAccount(formFields);
        }, 1500);
      } else {
        this.switchLoadingTo(false);
      }
    },

    validatePassword(event) {
      const password = event.target.value;
      this.password = password;
      this.validatePasswordField(password);
      this.validateConfirmPasswordField(password);
    },

    validateMatchingPassword(event) {
      const matchingPassword = event.target.value;
      this.password_confirmation = matchingPassword;

      this.validateConfirmPasswordField(matchingPassword);
    },

    getFormFields() {
      return {
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        password: this.password,
        password_confirmation: this.password_confirmation,
        var_referral: this.varReseller,
        referer: this.referer,
        urlReferer: this.urlReferer,
        phone: this.$refs.phone.value,
        company: this.$refs.company.value,
      };
    },

    async validateForm(fields) {
      this.clearErrors();
      const paramLabels = {
        first_name: "Fist Name",
        last_name: "Last Name",
        company: "Organization Name",
        email: "Email",
        phone: "Phone",
        password: "Password",
        password_confirmation: "Password Confirmation",
      };

      Object.keys(paramLabels).forEach((paramKey) => {
        this.validateFieldEmpty(
          paramKey,
          fields[paramKey],
          paramLabels[paramKey]
        );
        this.validateFieldLength(
          paramKey,
          fields[paramKey],
          paramLabels[paramKey]
        );
      });

      if (fields.phone) {
        const numbers = /^\d+(-\d+)*$/;
        if (!fields.phone.match(numbers)) {
          this.pushError(`Check the phone format.`);
        }
      }

      // Validate password
      this.validatePasswordField(fields.password);
      this.validateConfirmPasswordField(fields.password_confirmation);
      this.pushPasswordErrorFromRules();

      // Validate email
      await this.validateEmailField(fields.email);
    },

    validateFieldEmpty(paramKey, fieldValue, paramLabel) {
      const msg = "can't be empty.";

      if (!fieldValue) {
        this.pushError(`${paramLabel} ${msg}`);
      }
    },

    validateFieldLength(paramKey, fieldValue, paramLabel) {
      const msg = "the max amount of characters are 255.";

      if (fieldValue.length > 256) {
        this.pushError(`${paramLabel} ${msg}`);
      }
    },

    validatePasswordField(password) {
      const upper = /[A-Z]/m;
      const lower = /[a-z]/m;
      const digits = /\d/m;
      const symbols = /[\W_]/m;

      this.passwordRules.uppercase = upper.test(password);
      this.passwordRules.lowercase = lower.test(password);
      this.passwordRules.digits = digits.test(password);
      this.passwordRules.symbol = symbols.test(password);
      this.passwordRules.amount = password.length > 7;
    },

    validateConfirmPasswordField(matchingPassword) {
      this.passwordRules.matching =
        this.password.length > 0 &&
        this.password === this.password_confirmation &&
        this.password === matchingPassword;
    },

    pushPasswordErrorFromRules() {
      if (!this.passwordRules.uppercase) {
        this.pushError("Password is missing a capital letter.");
      }
      if (!this.passwordRules.lowercase) {
        this.pushError("Password is missing a lower case letter.");
      }
      if (!this.passwordRules.digits) {
        this.pushError("Password is missing a number.");
      }
      if (!this.passwordRules.symbol) {
        this.pushError("Password is missing a symbol.");
      }
      if (!this.passwordRules.amount) {
        this.pushError("Password must be at least 8 characters.");
      }
      if (!this.passwordRules.matching) {
        this.pushError("Password confirmation doesn't match.");
      }
    },

    async validateEmailField(email) {
      try {
        const data = { email };
        const response = await axios.post("/email-check", data);
        if (response.data.email) {
          this.pushError(`${email} already exist.`);
        }
      } catch (error) {
        // this should not be a problem to stop creating the testdrive.
      }
    },

    async createNewAccount(fields) {
      try {
        const response = await axios.post("/register-lead", fields);
        if (response.status === 200) {
          const urlPrefix = "/account/welcome-kit/view/testdrive";
          window.location.href = `${urlPrefix}-${response.data.testdrive_id}/${response.data.group}`;
        } else {
          this.switchLoadingTo(false);
          this.error_alert = true;
        }
      } catch (error) {
        this.switchLoadingTo(false);
        this.error_alert = true;
      }
    },

    notifyLoading() {
      swal.fire({
        title: "Processing...",
        showConfirmButton: false,
        allowOutsideClick: false,
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
    },

    notifyError() {
      swal.fire({
        type: "error",
        title: "Oops...",
        html:
          "<h5>Something went wrong!</h5><br/>" +
          "<h5>Please refresh the page and try again.</h5><br/>",
        showConfirmButton: true,
      });
    },

    // Because we are using async function we need to call this function inside.
    switchLoadingTo(load) {
      this.loading = load;
      if (!load) {
        swal.hideLoading();
        swal.close();
      }
    },
  },
};
</script>
